import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Typography,
  IconButton,
  Select,
  MenuItem,
  InputLabel,
  Grid,
  Switch,
  Checkbox,
  ListItemIcon,
  ListItemText,
  OutlinedInput,
} from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import { useSnackbar } from '../../../../../global/config/useSnackbar';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';

interface AdHocReportingRuleFormData {
  adHocReportingRuleId?: string;
  name: string;
  description?: string;
  isActive: boolean;
  matchType: string;
  programIds: string[];
  activityIds: string[];
  roleIds: string[];
  permissionLevels: string[];
}

interface Program {
  programId: string;
  name: string;
}

interface Activity {
  activityId: string;
  name: string;
  programId?: string;
}

interface ActivityRole {
  activityRoleId: string;
  name: string;
}

interface PermissionLevelOption {
  value: string;
  label: string;
}

interface ExistingAdHocReportingRule {
  adHocReportingRuleId: string;
  name: string;
  description?: string;
  isActive: boolean;
  matchType: string;
  programs: Program[];
  activities: Activity[];
  roles: ActivityRole[];
  permissionLevels: string[];
  createdAt: string;
  updatedAt?: string;
}

interface AdHocRuleDialogProps {
  open: boolean;
  onClose: () => void;
  rule: ExistingAdHocReportingRule | null;
  programs?: Program[];
  activities?: Activity[];
  roles?: ActivityRole[];
  permissionLevelOptions?: PermissionLevelOption[];
}

const SELECT_ALL_VALUE = 'all';
const SELECT_NONE_VALUE = 'none';

export function AdHocRuleDialog({
  open,
  onClose,
  rule,
  programs = [],
  activities = [],
  roles = [],
  permissionLevelOptions = [],
}: AdHocRuleDialogProps) {
  const [formData, setFormData] = useState<AdHocReportingRuleFormData>({
    name: '',
    description: '',
    isActive: true,
    matchType: 'all',
    programIds: [],
    activityIds: [],
    roleIds: [],
    permissionLevels: [],
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [saving, setSaving] = useState(false);
  const apiUrl = useApiUrl();
  const campfireFetch = useCampfireFetch({ defer: true });
  const { setSnackbar } = useSnackbar();
  const { theme } = useCampfireTheme();

  useEffect(() => {
    if (rule) {
      setFormData({
        adHocReportingRuleId: rule.adHocReportingRuleId,
        name: rule.name,
        description: rule.description || '',
        isActive: rule.isActive,
        matchType: rule.matchType,
        programIds: rule.programs.map((p) => p.programId),
        activityIds: rule.activities.map((a) => a.activityId),
        roleIds: rule.roles.map((r) => r.activityRoleId),
        permissionLevels: rule.permissionLevels || [],
      });
    } else {
      setFormData({
        name: '',
        description: '',
        isActive: true,
        matchType: 'all',
        programIds: [],
        activityIds: [],
        roleIds: [],
        permissionLevels: [],
      });
    }
    setErrors({});
  }, [rule, open]);

  const handleFieldChange = (field: keyof AdHocReportingRuleFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event?.target?.value || '';
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleMatchTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      matchType: event.target.value,
    }));
  };

  const handleProgramChange = (event: any) => {
    const {
      target: { value },
    } = event;
    if (value.includes(SELECT_NONE_VALUE)) {
      setFormData((prev) => ({ ...prev, programIds: [] }));
      return;
    }
    if (value.includes(SELECT_ALL_VALUE)) {
      setFormData((prev) => ({ ...prev, programIds: programs.map((p) => p.programId) }));
      return;
    }
    setFormData((prev) => ({ ...prev, programIds: value }));
  };

  const handleActivityChange = (event: any) => {
    const {
      target: { value },
    } = event;
    if (value.includes(SELECT_NONE_VALUE)) {
      setFormData((prev) => ({ ...prev, activityIds: [] }));
      return;
    }
    const filteredActivities =
      formData.programIds.length > 0
        ? activities.filter((a) => a.programId && formData.programIds.includes(a.programId))
        : activities;
    if (value.includes(SELECT_ALL_VALUE)) {
      setFormData((prev) => ({ ...prev, activityIds: filteredActivities.map((a) => a.activityId) }));
      return;
    }
    setFormData((prev) => ({ ...prev, activityIds: value }));
  };

  const handleRoleChange = (event: any) => {
    const {
      target: { value },
    } = event;
    if (value.includes(SELECT_NONE_VALUE)) {
      setFormData((prev) => ({ ...prev, roleIds: [] }));
      return;
    }
    if (value.includes(SELECT_ALL_VALUE)) {
      setFormData((prev) => ({ ...prev, roleIds: roles.map((r) => r.activityRoleId) }));
      return;
    }
    setFormData((prev) => ({ ...prev, roleIds: value }));
  };

  const handlePermissionLevelChange = (event: any) => {
    const {
      target: { value },
    } = event;
    if (value.includes(SELECT_NONE_VALUE)) {
      setFormData((prev) => ({ ...prev, permissionLevels: [] }));
      return;
    }
    if (value.includes(SELECT_ALL_VALUE)) {
      setFormData((prev) => ({ ...prev, permissionLevels: permissionLevelOptions.map((p) => p.value) }));
      return;
    }
    setFormData((prev) => ({ ...prev, permissionLevels: value }));
  };

  const validate = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Rule name is required';
    }

    const hasCriteria =
      formData.programIds.length > 0 ||
      formData.activityIds.length > 0 ||
      formData.roleIds.length > 0 ||
      formData.permissionLevels.length > 0;
    if (!hasCriteria) {
      newErrors.criteria = 'At least one criterion is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      return;
    }

    setSaving(true);

    try {
      await campfireFetch.run({
        url: `${apiUrl}/vm/admin-console/ad-hoc-rules/save`,
        method: 'post',
        data: {
          id: formData.adHocReportingRuleId || null,
          name: formData.name,
          description: formData.description || null,
          matchType: formData.matchType,
          isActive: formData.isActive,
          programIds: formData.programIds,
          activityIds: formData.activityIds,
          roleIds: formData.roleIds,
          permissionLevels: formData.permissionLevels,
        },
      });

      setSnackbar({
        open: true,
        message: rule ? 'Rule updated successfully' : 'Rule created successfully',
        variant: 'success',
      });

      onClose();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error saving rule',
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const isEditing = !!rule;

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle style={{ paddingBottom: 0 }}>
        <Grid container justify='space-between'>
          <Grid item>
            <Box alignItems='center'>
              <Typography
                style={{
                  fontSize: '23px',
                  fontWeight: 550,
                  marginRight: '16px',
                  color: theme.color.grey.neutral400,
                  paddingLeft: '5px',
                }}
              >
                {isEditing ? 'Edit Ad-Hoc Reporting Rule' : 'Create Ad-Hoc Reporting Rule'}
              </Typography>
            </Box>
          </Grid>
          <Grid item>
            <Box display='flex' alignItems='center'>
              <IconButton key='close' aria-label='Close' color='inherit' onClick={onClose}>
                <CloseIcon color='action' />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
        <Box pr={3} mt={1}>
          <Typography style={{ fontSize: 14, paddingLeft: '5px', paddingRight: '10px' }} color='textSecondary'>
            Configure rules to determine which volunteers can access ad-hoc reporting features.
            <br /> Hit &apos;Cancel&apos; to close this window.
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent style={{ padding: '12px 24px 16px 24px', minHeight: '400px', minWidth: '500px' }}>
        <Box display='flex' flexDirection='column' style={{ gap: '24px' }}>
          <Box>
            <Typography
              style={{ fontSize: '14px', fontWeight: 500, marginBottom: '12px', color: theme.color.grey.neutral400 }}
            >
              Rule Details
            </Typography>
            <TextField
              fullWidth
              label='Rule Name'
              value={formData.name}
              onChange={handleFieldChange('name')}
              error={!!errors.name}
              helperText={errors.name}
              required
              variant='outlined'
              style={{ marginBottom: '16px' }}
            />
            <TextField
              fullWidth
              label='Description'
              value={formData.description || ''}
              onChange={handleFieldChange('description')}
              multiline
              rows={2}
              variant='outlined'
              helperText='Optional description to explain the purpose of this rule'
            />
          </Box>

          <Box>
            <Typography
              style={{ fontSize: '14px', fontWeight: 500, marginBottom: '12px', color: theme.color.grey.neutral400 }}
            >
              Match Type
            </Typography>
            <FormControl component='fieldset'>
              <RadioGroup row value={formData.matchType} onChange={handleMatchTypeChange}>
                <FormControlLabel value='all' control={<Radio color='primary' />} label='Match All Criteria' />
                <FormControlLabel value='any' control={<Radio color='primary' />} label='Match Any Criteria' />
              </RadioGroup>
            </FormControl>
          </Box>

          <Box>
            <Typography
              style={{ fontSize: '14px', fontWeight: 500, marginBottom: '12px', color: theme.color.grey.neutral400 }}
            >
              Criteria
            </Typography>

            {errors.criteria && (
              <Typography color='error' variant='body2' style={{ marginBottom: '12px' }}>
                {errors.criteria}
              </Typography>
            )}

            {/* Program Criteria */}
            <Box mb={3}>
              <Typography style={{ fontSize: '12px', fontWeight: 500, color: '#000000b0', paddingBottom: '16px' }}>
                PROGRAMS
              </Typography>
              {programs.length ? (
                <FormControl fullWidth variant='outlined'>
                  <InputLabel>Select Programs</InputLabel>
                  <Select
                    multiple
                    value={formData.programIds}
                    onChange={handleProgramChange}
                    input={<OutlinedInput label='Select Programs' />}
                    renderValue={(selected) => {
                      const selectedIds = selected as string[];
                      if (selectedIds.length === programs.length) return 'All programs';
                      if (selectedIds.length === 0) return 'No programs selected';
                      return programs
                        .filter((p) => selectedIds.includes(p.programId))
                        .map((p) => p.name)
                        .join(', ');
                    }}
                  >
                    <MenuItem value={SELECT_ALL_VALUE}>
                      <ListItemText primary='Select All' />
                    </MenuItem>
                    <MenuItem value={SELECT_NONE_VALUE}>
                      <ListItemText primary='Deselect All' />
                    </MenuItem>
                    {programs.map((program) => (
                      <MenuItem key={program.programId} value={program.programId}>
                        <ListItemIcon>
                          <Checkbox color='primary' checked={formData.programIds.includes(program.programId)} />
                        </ListItemIcon>
                        <ListItemText primary={program.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Typography variant='body2' color='textSecondary'>
                  No programs available
                </Typography>
              )}
            </Box>

            {/* Activity Criteria */}
            <Box mb={3}>
              <Typography style={{ fontSize: '12px', fontWeight: 500, color: '#000000b0', paddingBottom: '16px' }}>
                ACTIVITIES
              </Typography>
              {activities.length ? (
                <FormControl fullWidth variant='outlined'>
                  <InputLabel>Select Activities</InputLabel>
                  <Select
                    multiple
                    value={formData.activityIds}
                    onChange={handleActivityChange}
                    input={<OutlinedInput label='Select Activities' />}
                    renderValue={(selected) => {
                      const selectedIds = selected as string[];
                      const filteredActivities =
                        formData.programIds.length > 0
                          ? activities.filter((a) => a.programId && formData.programIds.includes(a.programId))
                          : activities;
                      if (selectedIds.length === filteredActivities.length) return 'All activities';
                      if (selectedIds.length === 0) return 'No activities selected';
                      return activities
                        .filter((a) => selectedIds.includes(a.activityId))
                        .map((a) => a.name)
                        .join(', ');
                    }}
                  >
                    <MenuItem value={SELECT_ALL_VALUE}>
                      <ListItemText primary='Select All' />
                    </MenuItem>
                    <MenuItem value={SELECT_NONE_VALUE}>
                      <ListItemText primary='Deselect All' />
                    </MenuItem>
                    {(formData.programIds.length > 0
                      ? activities.filter((a) => a.programId && formData.programIds.includes(a.programId))
                      : activities
                    ).map((activity) => (
                      <MenuItem key={activity.activityId} value={activity.activityId}>
                        <ListItemIcon>
                          <Checkbox color='primary' checked={formData.activityIds.includes(activity.activityId)} />
                        </ListItemIcon>
                        <ListItemText primary={activity.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Typography variant='body2' color='textSecondary'>
                  No activities available
                </Typography>
              )}
            </Box>

            {/* Activity Role Criteria */}
            <Box mb={3}>
              <Typography style={{ fontSize: '12px', fontWeight: 500, color: '#000000b0', paddingBottom: '16px' }}>
                ACTIVITY ROLES
              </Typography>
              {roles.length ? (
                <FormControl fullWidth variant='outlined'>
                  <InputLabel>Select Roles</InputLabel>
                  <Select
                    multiple
                    value={formData.roleIds}
                    onChange={handleRoleChange}
                    input={<OutlinedInput label='Select Roles' />}
                    renderValue={(selected) => {
                      const selectedIds = selected as string[];
                      if (selectedIds.length === roles.length) return 'All roles';
                      if (selectedIds.length === 0) return 'No roles selected';
                      return roles
                        .filter((r) => selectedIds.includes(r.activityRoleId))
                        .map((r) => r.name)
                        .join(', ');
                    }}
                  >
                    <MenuItem value={SELECT_ALL_VALUE}>
                      <ListItemText primary='Select All' />
                    </MenuItem>
                    <MenuItem value={SELECT_NONE_VALUE}>
                      <ListItemText primary='Deselect All' />
                    </MenuItem>
                    {roles.map((role) => (
                      <MenuItem key={role.activityRoleId} value={role.activityRoleId}>
                        <ListItemIcon>
                          <Checkbox color='primary' checked={formData.roleIds.includes(role.activityRoleId)} />
                        </ListItemIcon>
                        <ListItemText primary={role.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Typography variant='body2' color='textSecondary'>
                  No roles available
                </Typography>
              )}
            </Box>

            {/* Permission Level Criteria */}
            <Box mb={3}>
              <Typography style={{ fontSize: '12px', fontWeight: 500, color: '#000000b0', paddingBottom: '16px' }}>
                PERMISSION LEVELS
              </Typography>
              {permissionLevelOptions.length ? (
                <FormControl fullWidth variant='outlined'>
                  <InputLabel>Select Permission Levels</InputLabel>
                  <Select
                    multiple
                    value={formData.permissionLevels}
                    onChange={handlePermissionLevelChange}
                    input={<OutlinedInput label='Select Permission Levels' />}
                    renderValue={(selected) => {
                      const selectedIds = selected as string[];
                      if (selectedIds.length === permissionLevelOptions.length) return 'All permission levels';
                      if (selectedIds.length === 0) return 'No permission levels selected';
                      return permissionLevelOptions
                        .filter((p) => selectedIds.includes(p.value))
                        .map((p) => p.label)
                        .join(', ');
                    }}
                  >
                    <MenuItem value={SELECT_ALL_VALUE}>
                      <ListItemText primary='Select All' />
                    </MenuItem>
                    <MenuItem value={SELECT_NONE_VALUE}>
                      <ListItemText primary='Deselect All' />
                    </MenuItem>
                    {permissionLevelOptions.map((level) => (
                      <MenuItem key={level.value} value={level.value}>
                        <ListItemIcon>
                          <Checkbox color='primary' checked={formData.permissionLevels.includes(level.value)} />
                        </ListItemIcon>
                        <ListItemText primary={level.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Typography variant='body2' color='textSecondary'>
                  No permission levels available
                </Typography>
              )}
            </Box>
          </Box>

          <Box display='flex' justifyContent='space-between' alignItems='center' style={{ padding: '16px 0' }}>
            <Box>
              <Typography style={{ fontSize: '14px', fontWeight: 500, color: theme.color.grey.neutral400 }}>
                Enable Rule
              </Typography>
              <Typography style={{ fontSize: '13px', color: theme.color.grey.neutral300 }}>
                When enabled, this rule will be applied to determine ad-hoc reporting access.
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.checked }))}
                  color='primary'
                  disabled={saving}
                />
              }
              label=''
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Box padding={2}>
          <TabletButton variant='text' color='error' onClick={onClose} disabled={saving} style={{ marginRight: 8 }}>
            Cancel
          </TabletButton>
          <TabletButton variant='contained' color='primary' onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : isEditing ? 'Update Rule' : 'Create Rule'}
          </TabletButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
